import { Switch } from 'antd';
import type { SwitchProps } from 'antd';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';

/**
 * Props {@link SwitchBox}.
 */
export interface SwitchBoxProps extends Omit<SwitchProps, 'size'> {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu. */
  size?: SwitchProps['size'];
}

/**
 * Switch (toggle) — wrapper Antd Switch.
 *
 * Antd Switch hanya punya 2 size: `default` & `small` — tidak ada `middle`/
 * `large`. Mapping context:
 * - `page`  → `small`
 * - `form`/`drawer` → `default`
 *
 * Class `booku-control` → vertical center alignment.
 *
 * Pemakaian:
 * ```tsx
 * <SwitchBox checked={aktif} onChange={setAktif} />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
export const SwitchBox = forwardRef<HTMLButtonElement, SwitchBoxProps>(
  function SwitchBox({ context = 'form', size, className, ...rest }, ref) {
    // Mapping ke Switch size (hanya 'default' | 'small').
    const resolvedSize: SwitchProps['size'] =
      size ?? (controlSizeForContext(context) === 'small' ? 'small' : 'default');
    return (
      <Switch
        ref={ref}
        size={resolvedSize}
        className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
        {...rest}
      />
    );
  }
);
